import { type InputCapture } from '@canvas/input';

import { type SelectionsMachine } from '../../machine/selection-machine.ts';
import { type SharedEventLoop } from '../event-loop.ts';
import { type SelectionUiInteraction } from '../selection-ui-interaction.ts';
import { type SelectionInput } from '../types.ts';

import { type MouseInputAdapter } from './mouse-input-adapter.ts';
import { createMouseStateMachine } from './state-machine/mouse-state-machine.ts';

export type MouseRowViewportBounds = {
  getMaxVisibleRowIndex: () => number;
};

export type CreateMouseInputOptions = {
  sharedLoop: SharedEventLoop;
  adapter: MouseInputAdapter;
  viewportBounds: MouseRowViewportBounds;
  getVisibleEntryCount: () => number;
  /**
   * When set, forwards selection UI telemetry from the mouse state machine to the caller.
   */
  onSelectionUiInteraction?: (event: SelectionUiInteraction) => void;
};

/**
 * Converts shared pointer captures into selection, drag, edit, and fill events
 * through the focused mouse state machine.
 *
 * Renderer-specific hit testing and scroll geometry are supplied through `adapter`; the input
 * owns no UI or database state.
 * Setup subscribes once to the shared event loop; disposal unsubscribes with the
 * recyclable selection lifecycle.
 */
export function createMouseInput(
  machine: SelectionsMachine,
  inputCapture: InputCapture,
  options: CreateMouseInputOptions
): SelectionInput {
  const {
    sharedLoop,
    adapter,
    viewportBounds,
    getVisibleEntryCount,
    onSelectionUiInteraction
  } = options;

  let currentState = createMouseStateMachine({
    machine,
    inputCapture,
    getClickTarget: adapter.hitTest,
    getScrollContainer: adapter.getScrollContainer,
    getHorizontalAutoScrollViewportLeft: adapter.getHorizontalAutoScrollViewportLeft,
    getMaxVisibleRowIndex: viewportBounds.getMaxVisibleRowIndex,
    getVisibleEntryCount,
    onSelectionUiInteraction
  });
  let unsubscribe: () => void;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribe((events) => {
        for (const event of events) {
          if (event.type === 'pointerDown') {
            currentState = currentState.onPointerDown(event);
          } else if (event.type === 'pointerMove') {
            currentState = currentState.onPointerMove(event);
          } else if (event.type === 'pointerUp') {
            currentState = currentState.onPointerUp(event);
          }
        }
      });
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
